/* eslint-disable max-lines */
import { useContext, useEffect, useRef, useState } from 'react';
import { Button, Grid, Typography, Box, LinearProgress, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/system';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { Buffer } from 'buffer';
import ErrorIcon from '../../images/ErrorX.svg';
import ErrorTriangleIcon from '../../images/ErrorTriangle.svg';
import DoneTick from '../../images/Done.svg';
import DoneTickGreen from '../../images/Done-green.svg';
import NewAddressImage from '../../images/Upload_GIF_1.gif';
import NewAddressSpanish from '../../images/Upload_GIF_1ES.gif';
import { SidebarStyles as classes } from './SidebarStyles';
import LeadGenerationContext from '../../context/LeadGenerationContext';
import cloudUpload from '../../images/cloud-upload.svg';
import uploadBtnIcon from '../../images/upload_btn_icon.svg';
import cameraBtnIcon from '../../images/camera_upload_icon.svg';
import CustomTooltip from '../Tooltip/CustomTooltip';
import Loader from '../Loader/Loader';
import {
  ADDRESSLINE1_MAX_ERR,
  ADDRESSLINE2_MAX_ERR,
  DONE,
  FAILED,
  HOME_PR,
  HOME_VI,
  MANDATORY_FIELD_ERR,
  NAME_MISMATCH_ERROR,
  ORIENTAL_ORANGE,
  UPLOAD_ADDRESS_GIF_TEXT,
} from '../../constants';
import { LayoutStyles as bundleClasses } from '../Bundle/LayoutStyles';
import {
  generateFileMD5,
  uploadDocument,
  uploadDocumentByPreSignedURL,
  uploadDocumentXL,
} from '../../services/uploadService';
import { commonUtility, commonUtilityValueReturn } from '../LeadGeneration/UtilityFunctions';
import { getOcrAddressValidation } from '../../services/formService';
import { productMapping } from '../../services/productMapping';
import { productNameURLMapping } from '../../services/productNameURLMapping';
import { businessMapping } from '../../services/businessMapping';
import { dgiMapping } from '../../services/dgiMapping';
import {
  clearUploadFocus,
  commonTernary,
  getRoot,
  isBundle,
  isCDIraproduct,
  isIRAEducativaProduct,
  isWebComponentFlow,
  zipCodeValueSetError,
} from '../commonCode';
import BillTypeCustomCard from '../Bundle/BillTypeCustomCard';
import {
  CameraUpload,
  CloudUpload,
  CloudUploadBtn,
  DeleteIcon,
  FileIcon,
  GreenTickFill,
} from '../../images/svg/CustomizedSVG';

const getDataResponse = async (response, file, fileMd5Base64, fileType1, baseContext, props) => {
  const { setCmAddressDocLoading } = baseContext;
  const { setUploadStatus, setUploadData, setUploadDataError } = props;
  if (response.data) {
    const documentId = commonUtility(response.data.documentId);
    const preSignedUrl = commonUtility(response.data.preSignedUrl);
    if (preSignedUrl && documentId) {
      const resp = await uploadDocumentByPreSignedURL(
        preSignedUrl,
        file,
        fileMd5Base64,
        baseContext.setTaxUploadProgress,
        baseContext.setTaxLinearProgress,
        fileType1,
        { commonDocUpload: true }
      );
      if (resp && resp.status === 200) {
        setUploadData((prevState) => ({
          ...prevState,
          document: documentId,
        }));
        setUploadDataError((prevState) => ({
          ...prevState,
          document: '',
        }));
        setCmAddressDocLoading((prev) => ({ ...prev, [fileType1]: false }));
        setUploadStatus(DONE);
      } else {
        setCmAddressDocLoading((prev) => ({ ...prev, [fileType1]: false }));
        setUploadStatus(FAILED);
      }
    }
  }
};

const documentResponse = async (...args) => {
  const fileMD5 = args[0];
  const fileExtension = args[1];
  const fileName = args[2];
  const fileType1 = args[3];
  const fileSize = args[4];
  const file = args[5];
  const baseContext = args[6];
  const props = args[7];
  const { setCmAddressDocLoading } = baseContext;
  const { setUploadStatus } = props;
  if (fileMD5) {
    const fileMd5Base64 = Buffer.from(fileMD5, 'hex').toString('base64');
    const response = await uploadDocumentXL(
      fileExtension,
      fileName,
      fileType1,
      fileSize,
      fileMd5Base64,
      baseContext.accessToken,
      baseContext.setTaxUploadProgress,
      baseContext.setTaxLinearProgress,
      { isWCFlow: baseContext?.wcFlow, env: baseContext?.environment, commonDocUpload: true }
    );

    if (response?.status === 201) {
      await getDataResponse(response, file, fileMd5Base64, fileType1, baseContext, props);
    } else {
      setCmAddressDocLoading((prev) => ({ ...prev, [fileType1]: false }));
      setUploadStatus(FAILED);
    }
  } else {
    setCmAddressDocLoading((prev) => ({ ...prev, [fileType1]: false }));
    setUploadStatus(FAILED);
  }
};
export const commonUploadFile = async (file, baseContext, props) => {
  const { fileType, setUploadStatus, setUploadData, setUploadDataError, setUploadFileData } = props;
  const { setCmAddressDocLoading } = baseContext;
  setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: true }));

  setUploadFileData((prev) => ({ ...prev, name: file.name, size: file.size, type: file.type }));

  const fileName = file.name;
  const fileParts = fileName.split('.');
  const fileExtension = fileParts[fileParts.length - 1].toLowerCase();

  const fileSize = file.size;

  if (fileSize > 10485760) {
    setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
    setUploadData((prevState) => ({ ...prevState, ocrErrorMessage: '' }));
    setUploadDataError((prevState) => ({
      ...prevState,
      ocrUIErrors: `The file exceeds the maximum upload size (10 Mb).`,
    }));
    setUploadStatus(FAILED);
    return;
  }

  const extensions = ['pdf', 'jpg', 'jpeg', 'png'];

  let isValidExtension = false;
  if (extensions.includes(fileExtension.toLowerCase())) isValidExtension = true;
  if (!isValidExtension) {
    setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
    setUploadStatus(FAILED);
    setUploadDataError((prevState) => ({
      ...prevState,
      ocrUIErrors: 'Only files with the following formats are allowed: PNG, JPG, PDF.',
    }));
    return;
  }

  const formData = new FormData();
  formData.append('file', file);

  if (fileSize > 3145728) {
    const fileMD5 = await generateFileMD5(file);
    await documentResponse(
      fileMD5,
      fileExtension,
      fileName,
      fileType,
      fileSize,
      file,
      baseContext,
      props
    );
  } else {
    const response = await uploadDocument(
      fileExtension,
      fileName,
      fileType,
      formData,
      baseContext.accessToken,
      baseContext.setTaxUploadProgress,
      baseContext.setTaxLinearProgress,
      { isWCFlow: baseContext?.wcFlow, env: baseContext?.environment, commonDocUpload: true }
    );

    if (response?.data) {
      setUploadData((prevState) => ({
        ...prevState,
        document: response.data.documentId,
        ...(baseContext.currentPage === 'upload-documents-later' && {
          [fileType]: response.data.documentId,
        }),
      }));
      setUploadDataError((prevState) => ({
        ...prevState,
        document: '',
        ...(baseContext.currentPage === 'upload-documents-later' && { [fileType]: '' }),
      }));

      setUploadStatus(DONE);
      setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
    } else {
      setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
      setUploadStatus(FAILED);
    }
  }
};

function CommonDocumentUpload(props) {
  const { t } = useTranslation();
  const leadGenerationContext = useContext(LeadGenerationContext);
  const baseContext = isWebComponentFlow();
  const {
    productName,
    addressInformation,
    setAddressInformation,
    setIsOcrErrMessageFound,
    isOcrErrMessageFound,
    location,
    cmAddressDocLoading,
    isLightTheme,
    theme,
    setProductDataIRA,
    taxUploadProgress,
    setTaxUploadProgress,
    setTaxLinearProgress,
    setIsScreenFiveDirty,
  } = baseContext;
  const fileInput = useRef(null);
  const {
    vouchedAddrEdited,
    history,
    pagePath,
    isBundleFlag,
    handleChange,
    setUploadData,
    uploadStatus,
    uploadDocHeading,
    setUploadStatus,
    setUploadDataError,
    productDataIRAErrors,
    toolTipData,
    uploadError,
    uploadFileData,
    setUploadFileData,
    fileType,
    uploadData,
    columnLayout,
  } = props;
  const [checkFlow, setCheckFlow] = useState('');
  useEffect(() => {
    if (businessMapping(productName)) {
      setCheckFlow('commercial-form');
    } else {
      setCheckFlow('retail-form');
    }
  }, []);

  const themes = useTheme();
  const isMobile = useMediaQuery(themes?.breakpoints?.down(600), {
    defaultMatches: true,
  });

  const checkFileInput = () => {
    if (fileInput.current.value) {
      fileInput.current.value = '';
    }
  };
  const buttonClickHandler = (e) => {
    setIsScreenFiveDirty(true);
    e.preventDefault();

    checkFileInput();
    fileInput.current?.click();

    setUploadData((prevState) => ({
      ...prevState,
      ocrErrMessage: '',
    }));
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      const file = files[0];
      setIsScreenFiveDirty(true);
      setUploadData((prevState) => ({
        ...prevState,
        ocrErrMessage: '',
        file,
      }));
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      fileInput.current.files = dataTransfer.files;
      const event = new Event('change', { bubbles: true });
      fileInput.current.dispatchEvent(event);
      await commonUploadFile(file, baseContext, {
        fileType,
        setUploadStatus,
        setUploadData,
        setUploadDataError,
        setUploadFileData,
      });
    }
  };
  const fileInputHandler = async (e) => {
    setIsScreenFiveDirty(true);
    e.preventDefault();
    const file = e.target.files[0];
    if (file !== undefined && file !== null) {
      await commonUploadFile(file, baseContext, props);
    }
  };

  let addrUploadJsx;

  const ErrorUploadIcon = commonUtilityValueReturn(
    isBundle(productName),
    ErrorTriangleIcon,
    ErrorIcon
  );

  const addressProofUploadFail = () => {
    if (uploadStatus === FAILED && !cmAddressDocLoading[fileType]) {
      addrUploadJsx = (
        <Box component="div" sx={classes.statusContainer} tabIndex="0" width="100%">
          <img
            id="uploadAddressProofFail"
            src={ErrorUploadIcon}
            data-testid="upload_failed_id"
            onClick={(e) => buttonClickHandler(e)}
            onKeyDown={(e) => buttonClickHandler(e)}
            alt="Upload Failed"
            height="54px"
            width="54px"
          />
          <Box
            component="span"
            sx={[
              classes.errorText,
              ...(isBundle(productName)
                ? [{ fontSize: '20px', fontWeight: 700, color: '#D5151F', padding: '0 0 10px 0' }]
                : isCDIraproduct(productName)
                ? [{ textDecoration: 'underline' }]
                : []),
            ]}
          >
            {commonTernary(
              isBundle(productName),
              t('Oops! There was an error', {
                defaultValue: 'Oops! There was an error',
              }),
              isIRAEducativaProduct(productName)
                ? t('RelTypeTry', { defaultValue: 'Try Again' })
                : t('Try Again', { defaultValue: 'Try Again' })
            )}
          </Box>
        </Box>
      );
    }
    return addrUploadJsx;
  };

  const acceptFileType = () =>
    '.pdf,.jpg,.jpeg,.png,.jfif,.bmp,.bpm,.pgm,.ppm,.gif,.gifv,.tiff,.heic,.heif,.hevc,.mpg,.mpeg';
  useEffect(() => {
    let element = '';
    element = getRoot().getElementById(`${fileType}UploadContainer`);
    if (element) {
      if (isBundle(productName)) {
        if (uploadStatus === DONE && !cmAddressDocLoading[fileType]) {
          const backgroundColor = baseContext.isLightTheme ? '#E7F3E6' : '#0B6C0333';
          element.setAttribute(
            'style',
            `background-image: repeating-linear-gradient(0deg,#76BB70,#76BB70 7px,transparent 8px,transparent 15px),
                        repeating-linear-gradient(90deg,#76BB70,#76BB70 7px,transparent 8px,transparent 15px),
                        repeating-linear-gradient(180deg,#76BB70,#76BB70 7px,transparent 8px,transparent 15px),
                        repeating-linear-gradient(270deg,#76BB70,#76BB70 7px,transparent 8px,transparent 15px) !important;
     background: ${backgroundColor};
     background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;
     background-repeat: no-repeat;
     background-position: 0 0, 0 0, 100% 0, 0 100%`
          );
        } else if (uploadStatus === FAILED && !cmAddressDocLoading[fileType]) {
          const backgroundColor = baseContext.isLightTheme ? '#FBE8E9' : '#D5151F1A';
          element.setAttribute(
            'style',
            `background-image: repeating-linear-gradient(0deg,#FF6971,#FF6971 7px,transparent 8px,transparent 15px),repeating-linear-gradient(90deg,#FF6971,#FF6971 7px,transparent 8px,transparent 15px),repeating-linear-gradient(180deg,#FF6971,#FF6971 7px,transparent 8px,transparent 15px),repeating-linear-gradient(270deg,#FF6971,#FF6971 7px,transparent 8px,transparent 15px) !important;  background: ${backgroundColor};background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;background-repeat: no-repeat;background-position: 0 0, 0 0, 100% 0, 0 100%`
          );
        }
      } else if (uploadStatus === DONE && !cmAddressDocLoading[fileType]) {
        element.setAttribute(
          'style',
          'background-image: repeating-linear-gradient(0deg,#E37617,#E37617 7px,transparent 8px,transparent 15px),repeating-linear-gradient(90deg,#E37617,#E37617 7px,transparent 8px,transparent 15px),repeating-linear-gradient(180deg,#E37617,#E37617 7px,transparent 8px,transparent 15px),repeating-linear-gradient(270deg,#E37617,#E37617 7px,transparent 8px,transparent 15px) !important; background:#FFFDF2;background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;background-repeat: no-repeat;background-position: 0 0, 0 0, 100% 0, 0 100%'
        );
      } else if (uploadStatus === FAILED && !cmAddressDocLoading[fileType]) {
        element.setAttribute(
          'style',
          'background-image: repeating-linear-gradient(0deg,#D71721,#D71721 7px,transparent 8px,transparent 15px),repeating-linear-gradient(90deg,#D71721,#D71721 7px,transparent 8px,transparent 15px),repeating-linear-gradient(180deg,#D71721,#D71721 7px,transparent 8px,transparent 15px),repeating-linear-gradient(270deg,#D71721,#D71721 7px,transparent 8px,transparent 15px) !important; background:#FFF2F2;background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;background-repeat: no-repeat;background-position: 0 0, 0 0, 100% 0, 0 100%'
        );
      } else {
        element?.setAttribute(
          'style',
          'background-image: repeating-linear-gradient(0deg,#808285,#808285 7px,transparent 8px,transparent 15px),repeating-linear-gradient(90deg,#808285,#808285 7px,transparent 8px,transparent 15px),repeating-linear-gradient(180deg,#808285,#808285 7px,transparent 8px,transparent 15px),repeating-linear-gradient(270deg,#808285,#808285 7px,transparent 8px,transparent 15px) !important; background:#F4F4F4;background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;background-repeat: no-repeat;background-position: 0 0, 0 0, 100% 0, 0 100%'
        );
      }
    }
  }, [uploadStatus, baseContext.isLightTheme]);

  const routeToCameraUpload = () => {
    clearUploadFocus();

    setIsOcrErrMessageFound(false);
    setAddressInformation((prevState) => ({
      ...prevState,
      ocrErrMessage: '',
    }));
    setAddressInformation((prevState) => ({
      ...prevState,
      ocrUIErrors: '',
    }));
    if (baseContext?.wcFlow) {
      history.push({
        pathname: 'camera-upload',
        redirectingPath: pagePath,
        pageProps: props,
        flow: checkFlow,
      });
    } else {
      history.push({
        pathname: `/${location?.toLowerCase()}/${baseContext.language?.toLowerCase()}/${checkFlow}/${productNameURLMapping(
          productName
        )}/camera-upload`,
        redirectingPath: pagePath,
        pageProps: props,
        flow: checkFlow,
      });
    }
  };

  const successUploadImg = commonUtilityValueReturn(isBundle(productName), DoneTickGreen, DoneTick);
  return (
    <Grid container>
      {/* <Grid size={{ xs: 12 }}>
        <Typography
          variant="h2"
          data-testid="address-proof"
          className={
            businessFlow
              ? `${classes.top_text_upload_id} ${classes.business_top_text_upload_id}`
              : classes.top_text_upload_id
          }
        >
          {t('Upload Address Proof', { defaultValue: 'Upload Address Proof' })}
          <span style={{ fontSize: '22px', marginLeft: '5px', color: ORIENTAL_ORANGE }}>*</span>
          <span style={{ marginLeft: '10px', opacity: 1 }}>
            <CustomTooltip
              placement="top"
              text={t(`PreviewAddressConsent`, {
                defaultValue: `Address proof is required to open your account if your current home address is not available on your ID. You will need to provide a utility bill with your name and address issued within the last 60 days.`,
              })}
            />
          </span>
        </Typography>
      </Grid> */}

      <input
        type="file"
        data-testid="inp-test"
        accept={acceptFileType()}
        onChange={fileInputHandler}
        ref={fileInput}
        style={{ display: 'none' }}
      />

      <Grid size={{ xs: 12 }} sx={classes.id_upload_grid}>
        <Box
          component="div"
          style={{
            paddingTop: isBundle(productName) ? '0px' : '20px',
            width: '100%',
            marginBottom: baseContext.flowTypeETB === 'EtbFlow' ? '0px' : '10px',
          }}
          sx={pagePath === 'business-account-info' ? classes.busiAccWidth : ''}
        >
          <Box sx={classes.top_text_upload_id}>
            <>
              <Typography
                component="span"
                variant="h2"
                data-testid="address-proof"
                sx={{
                  ...classes.top_text_upload_id,
                  whiteSpace: 'nowrap',
                  display: 'inline',
                  '@media (max-width: 1160px)': { whiteSpace: 'normal' },
                }}
              >
                {t(uploadDocHeading, { defaultValue: uploadDocHeading })}
                <span style={{ fontSize: '22px', marginLeft: '5px', color: ORIENTAL_ORANGE }}>
                  *
                </span>
              </Typography>
              <Box
                component="span"
                sx={{
                  marginLeft: '10px',
                  opacity: 1,
                  '@media (max-width:400px)': { marginLeft: '0px' },
                }}
              >
                <CustomTooltip
                  placement="top"
                  text={t(toolTipData, {
                    defaultValue: toolTipData,
                  })}
                />
              </Box>
            </>
          </Box>

          <div style={{ marginTop: '5px' }}>
            <Box
              component="div"
              sx={[
                classes.documentUploadContainer,
                classes.uploadContainer,
                classes.box,
                classes.box_a,
                pagePath === 'business-account-info' ? classes.busiAccWidth_box : '',
                isBundle(productName)
                  ? {
                      backgroundColor: theme?.palette?.fieldsBackground?.main,
                      border: `1px solid ${theme?.palette?.fieldsBorder?.main}`,
                      color: theme?.palette?.secondary?.main,
                      '&:focus': {
                        backgroundImage:
                          'repeating-linear-gradient(0deg,blue,blue 7px,transparent 8px,transparent 15px), repeating-linear-gradient(90deg,blue,blue 7px,transparent 8px,transparent 15px), repeating-linear-gradient(180deg,blue,blue 7px,transparent 8px,transparent 15px), repeating-linear-gradient(270deg,blue,blue 7px,transparent 8px,transparent 15px)',
                        borderImage:
                          'repeating-linear-gradient(0deg, blue, blue 1px, transparent 1px, transparent 1px)',
                      },
                    }
                  : {
                      border: '1px dashed #808285',
                      background: '#F4F4F4',
                      '&:focus': {
                        backgroundImage:
                          'repeating-linear-gradient(0deg,blue,blue 7px,transparent 8px,transparent 15px), repeating-linear-gradient(90deg,blue,blue 7px,transparent 8px,transparent 15px), repeating-linear-gradient(180deg,blue,blue 7px,transparent 8px,transparent 15px), repeating-linear-gradient(270deg,blue,blue 7px,transparent 8px,transparent 15px)',
                        borderImage:
                          'repeating-linear-gradient(0deg, blue, blue 1px, transparent 1px, transparent 1px)',
                      },
                    },
              ]}
              id={`${fileType}UploadContainer`}
            >
              {cmAddressDocLoading[fileType] && (
                <Box component="div" sx={classes.errorImgPosition}>
                  <Loader />
                </Box>
              )}

              {uploadStatus === null && !cmAddressDocLoading[fileType] && (
                <Box
                  component="div"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  sx={{
                    ...classes.uploadSecondaryContainer,
                    flexDirection: columnLayout ? 'column' : 'row',
                    justifyContent: 'space-between',
                    '@media (max-width: 601px)': {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                    },
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    component="p"
                    pb={columnLayout ? 3 : 0}
                    sx={{
                      width: '100%',
                      textAlign: isMobile || columnLayout ? 'center' : 'start',
                    }}
                  >
                    {t('Drag and drop the file (or)', {
                      defaultValue: 'Drag and drop the file (or)',
                    })}
                  </Typography>
                  <Box component="div" sx={{ ...classes.btn_grid_containerira, width: '100%' }}>
                    <Button
                      disableFocusRipple
                      color="inherit"
                      id="uploadAddressProofInitial"
                      data-testid="null_testid"
                      onClick={(e) => buttonClickHandler(e)}
                      sx={[
                        classes.docBtnStyles,
                        {
                          border: `1.6px solid ${theme?.palette?.orientalPrimary?.main}`,
                          color: theme?.palette?.orientalPrimary?.main,
                        },
                      ]}
                    >
                      {t('Upload Now', { defaultValue: 'Upload Now' })}
                    </Button>
                    <Button
                      disableFocusRipple
                      color="inherit"
                      variant="outlined"
                      sx={[
                        { ...classes.docBtnStyles, maxWidth: '107px' },
                        {
                          border: `1.6px solid ${theme?.palette?.orientalPrimary?.main}`,
                          color: theme?.palette?.orientalPrimary?.main,
                        },
                      ]}
                      onClick={routeToCameraUpload}
                      data-testid="camera_testid"
                    >
                      {t('Camera', { defaultValue: 'Camera' })}
                    </Button>
                  </Box>
                </Box>
              )}
              {uploadStatus === DONE && !cmAddressDocLoading[fileType] && (
                <Box component="div" sx={classes.statusContainer}>
                  <img src={successUploadImg} height="54px" width="54px" alt="Upload successful" />
                  <Box
                    component="span"
                    sx={isBundle(productName) ? classes.doneTextBundle : classes.doneText}
                  >
                    {isBundle(productName)
                      ? t('Done!', { defaultValue: 'Done!' })
                      : isIRAEducativaProduct(productName)
                      ? t('RelTypeDone', { defaultValue: 'Done' })
                      : t('Done', { defaultValue: 'Done' })}
                  </Box>
                </Box>
              )}

              {addressProofUploadFail()}
            </Box>
            <Box component="div" sx={classes.supportedText}>
              <span
                style={{
                  paddingRight: '10px',
                  color:
                    isBundle(productName) &&
                    uploadStatus === FAILED &&
                    !cmAddressDocLoading[fileType]
                      ? '#FF6971'
                      : '#727578',
                }}
              >
                {t('Supported file types: PNG, JPG, PDF.', {
                  defaultValue: 'Supported file types: PNG, JPG, PDF.',
                  nsSeparator: '|',
                })}
              </span>
              <span
                style={{
                  paddingLeft: '10px',
                  color:
                    isBundle(productName) &&
                    uploadStatus === FAILED &&
                    !cmAddressDocLoading[fileType]
                      ? '#FF6971'
                      : '#727578',
                }}
              >
                {t('Maximum: 10MB', {
                  defaultValue: 'Maximum: 10MB',
                  nsSeparator: '|',
                })}
              </span>
            </Box>

            {uploadStatus === FAILED && !cmAddressDocLoading[fileType] && !isBundle(productName) && (
              <Box
                component="span"
                id="addressUploadId"
                sx={
                  isOcrErrMessageFound ||
                  (!addressInformation.ocrAddressVerified && productMapping(productName))
                    ? classes.ocrErrorStyle
                    : classes.errorStyle
                }
              >
                {isOcrErrMessageFound
                  ? t(addressInformation.ocrErrorMessage, {
                      defaultValue: addressInformation.ocrErrorMessage,
                      nsSeparator: '|',
                    })
                  : t(uploadError.ocrUIErrors, {
                      defaultValue: uploadError.ocrUIErrors,
                      nsSeparator: '|',
                    })}{' '}
                {uploadError.ocrUIErrors?.includes('Only files with the following formats') && (
                  <Box component="div" sx={classes.errorStyle}>
                    <Box
                      component="span"
                      sx={{ fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={(e) => buttonClickHandler(e)}
                      onKeyDown={(e) => buttonClickHandler(e)}
                      tabIndex={0}
                    >
                      {t('Click to upload again.', { defaultValue: 'Click to upload again.' })}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </div>
        </Box>
      </Grid>

      {baseContext?.taxUploadProgress[fileType] === 100 &&
        baseContext?.taxLinearProgress[fileType] &&
        uploadData?.document && (
          <Grid container spacing={2} my={2} sx={bundleClasses.uploadDone}>
            <Grid size={{ md: 1 }} sx={bundleClasses.verificationPending}>
              <FileIcon
                width="16px"
                height="21px"
                color={
                  uploadStatus === 'FAILED'
                    ? '#D5151F'
                    : uploadStatus === 'DONE' && !isLightTheme
                    ? '#FFFFFF'
                    : theme?.palette?.subtext?.main
                }
              />
            </Grid>
            <Grid size={{ md: 10 }}>
              <Typography component="h5" paddingY="5px" sx={bundleClasses.fileUploadName}>
                {uploadFileData?.name ? uploadFileData?.name : fileType}
              </Typography>
              {uploadFileData?.size && (
                <Typography component="p" sx={bundleClasses.fileUploadSize}>
                  {Math.floor(uploadFileData.size / 1024)} KB
                </Typography>
              )}
            </Grid>

            <Grid size={{ md: 1 }}>
              {baseContext?.taxUploadProgress[fileType] === 100 &&
                baseContext?.taxLinearProgress[fileType] && (
                  //   <GreenTickFill height="20px" width="20px" />
                  <IconButton
                    id="closeIconId"
                    name={uploadFileData?.name}
                    data-testid={`cancel_testid_${uploadFileData?.name}`}
                    aria-label="delete"
                    onClick={() => {
                      setUploadStatus(null);
                      setTaxUploadProgress((prev) => ({ ...prev, [fileType]: 0 }));
                      setTaxLinearProgress((prev) => ({ ...prev, [fileType]: false }));
                      setUploadData((prevState) => ({
                        ...prevState,
                        document: '',
                      }));
                      setUploadDataError((prevState) => ({
                        ...prevState,
                        document: '',
                      }));
                    }}
                    // onKeyDown={removeUploadedFile}
                    size="small"
                    color="inherit"
                  >
                    <DeleteIcon
                      color={
                        uploadStatus === 'FAILED'
                          ? '#D5151F'
                          : uploadStatus === 'DONE' && !isLightTheme
                          ? '#FFFFFF'
                          : theme?.palette?.subtext?.main
                      }
                    />
                  </IconButton>
                )}
            </Grid>
          </Grid>
        )}
    </Grid>
  );
}

CommonDocumentUpload.propTypes = {
  pagePath: PropTypes.string,
  uploadDocHeading: PropTypes.string,
  toolTipData: PropTypes.string,
  uploadStatus: PropTypes.string,
  vouchedAddrEdited: PropTypes.bool,
  isBundleFlag: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  handleChange: PropTypes.func,
  setUploadData: PropTypes.func,
  setUploadStatus: PropTypes.func,
  setUploadDataError: PropTypes.func,
  productDataIRAErrors: PropTypes.object,
  uploadError: PropTypes.object,
  uploadFileData: PropTypes.object,
  setUploadFileData: PropTypes.func,
  fileType: PropTypes.string,
  uploadData: PropTypes.string,
  columnLayout: PropTypes.bool,
};

export default withRouter(CommonDocumentUpload);
